import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { compose } from 'redux';

import { convertToFloatNumber, splitNumberByCommas } from 'components/AgGrid/helpers/numberFormat';

// TODO: REFACTOR FILE - move function which related to only Documents grid
//  MOSTLY RELATED TO DOCUMENTS GRID (WORKSPACE, INSIGHTS) - be careful

export const SYMBOL_FOR_EMPTY_CELL = '–';

export const GRID_SERVICE_KEYS = {
  PREVIEW_CHECKBOX_COLUMN_NAME: '__SERVICE_CHECKBOX_PREVIEW_COLUMN',
  PREVIEW_BTN_COLUMN_NAME: '__SERVICE_BTN_PREVIEW_COLUMN',
  CONTEXT_MENU_COLUMN_NAME: '__SERVICE_CONTEXT_MENU_COLUMN',
  COLUMNS_VISIBILITY_MENU: '__SERVICE_COLUMNS_VISIBILITY_MENU',
  COLUMNS_MASTER_VIEW: '__SERVICE_COLUMNS_MASTER_VIEW',
  COLUMNS_LINKED_ICON: '__SERVICE_COLUMNS_LINKED_ICON',
};

export const GRID_FILTER_BUTTONS = Object.freeze({
  APPLY: 'apply',
  CANCEL: 'cancel',
  RESET: 'reset',
});

export const isServiceColumn = (colId) => Object.values(GRID_SERVICE_KEYS).includes(colId);

const getHeaderTooltipProps = ({ headerComponentParams }) =>
  headerComponentParams
    ? { headerComponentParams: { ...headerComponentParams, headerTooltip: true } }
    : { headerComponentParams: { headerTooltip: true } };

export const addHeaderTooltipPropsForColDefs = (columns) =>
  columns.map((column) => ({
    ...column,
    ...getHeaderTooltipProps(column),
  }));

const tooltipComponentByType = ['string', 'select', 'static_select', 'extraSelect', 'date', 'links'];

const getTooltipProps = (column) => {
  const tooltipTypeExist = tooltipComponentByType.includes(column.type);

  return tooltipTypeExist
    ? {
        tooltipField: column.field,
      }
    : {};
};

export const addColDefaultParams = (columns) =>
  columns.map((column) => ({
    ...column,
    width: column.type === 'actions' ? 0 : 200, // width used for save default initial state and compare for differences - dont move
    ...getTooltipProps(column),
  }));

export const changeWidthIfMasterViewNotAvailable = (columns, hasMasterDetail) =>
  columns.map((column) =>
    column.field === GRID_SERVICE_KEYS.PREVIEW_CHECKBOX_COLUMN_NAME && !hasMasterDetail
      ? { ...column, maxWidth: 55 }
      : column,
  );

// invert in columns pinned value
export const invertPinnedValue = (columns = []) => {
  const invertPinnedValues = {
    left: 'right',
    right: 'left',
  };
  return columns.map(({ pinned, ...column }) => ({
    ...column,
    pinned: invertPinnedValues[pinned] || null,
    lockPosition: invertPinnedValues[pinned] || null,
  }));
};

// invert in ag-grid state pinned value (columnPinning: {leftColIds: [}, rightColIds: [},)
export const invertPinnedState = (state) => ({
  ...state,
  ...(state?.columnPinning
    ? {
        columnPinning: {
          leftColIds: state.columnPinning?.rightColIds || [],
          rightColIds: state.columnPinning?.leftColIds || [],
        },
      }
    : {}),
});

export const isEllipsisActive = (el) => el.offsetWidth < el.scrollWidth;

export const setExelModeFilter =
  (filterType, buttons) =>
  (columns = []) => {
    const defaultButtons = buttons || [GRID_FILTER_BUTTONS.APPLY, GRID_FILTER_BUTTONS.CANCEL];
    return columns.map((column) => {
      const up = { excelMode: filterType, buttons: defaultButtons, closeOnApply: true };
      const filterParams = column.filterParams ? { ...column.filterParams, ...up } : up;
      return { ...column, filterParams };
    });
  };

// native Map
export const errorStatusList = new Map([
  [1, 'warning'],
  [2, 'error'],
]);

export const getRowStatus = (params) => {
  if (!params?.data) return false;

  return errorStatusList.get(get(params, 'data.warning.error_level'));
};

export const ID_FOR_DETECT_CELL_OVERFLOW = 'containerForDetectCellOverflow';
export const SET_FILTER_HOVER_CLASSNAME = 'set-filter-hover';

export const mergeDifferenceBtwColumnsAdapter = (columns, orderedColumnConfig) => {
  const colMap = keyBy(columns, 'field');
  const currentColumnMap = keyBy(orderedColumnConfig, 'colId');

  const removedColumns = orderedColumnConfig.filter((col) => !colMap[col.colId]).map((col) => col.colId);

  const addedColumns = columns.filter((col) => !currentColumnMap[col.field]);

  const res = orderedColumnConfig
    .filter((state) => !removedColumns.includes(state.colId))
    .map((state) => ({ ...colMap[state.colId], ...state }));

  return [...res, ...addedColumns];
};

const suppressSelectAllShortcut = (params) => {
  const KEY_A = 'A';
  const { event } = params;
  const { key } = event;

  const keysToSuppress = [];
  if (event.ctrlKey || event.metaKey) {
    keysToSuppress.push(KEY_A);
  }
  return keysToSuppress.some((suppressedKey) => suppressedKey === key || key.toUpperCase() === suppressedKey);
};

export const suppressSelectAll = (columns) =>
  columns.map((col) => ({ ...col, suppressKeyboardEvent: suppressSelectAllShortcut }));

export const convertColumnDefsToGridState = (columns) =>
  columns.reduce(
    (acc, { colId, width, maxWidth, minWidth, flex, pinned, hide }) => {
      const currentWidth = Math.max(minWidth || 1, Math.min(maxWidth || 1000, width));

      acc.columnOrder.orderedColIds = [...acc.columnOrder.orderedColIds, colId];
      acc.columnSizing.columnSizingModel = [
        ...acc.columnSizing.columnSizingModel,
        { colId, flex, width: currentWidth },
      ];
      if (pinned) {
        if (pinned === 'left') {
          acc.columnPinning.leftColIds = [...acc.columnPinning.leftColIds, colId];
        }
        if (pinned === 'right') {
          acc.columnPinning.rightColIds = [...acc.columnPinning.rightColIds, colId];
        }
      }
      if (hide) {
        acc.columnVisibility.hiddenColIds = [...acc.columnVisibility.hiddenColIds, colId];
      }
      return acc;
    },
    {
      columnOrder: {
        orderedColIds: [],
      },
      columnPinning: {
        leftColIds: [],
        rightColIds: [],
      },
      columnSizing: {
        columnSizingModel: [],
      },
      columnVisibility: {
        hiddenColIds: [],
      },
    },
  );

export const normalizeAgGridColDefs = (data) =>
  data.map(({ hint = null, headerComponentParams = {}, ...props }) => ({
    ...props,
    headerComponentParams: {
      hint,
      ...headerComponentParams,
    },
  }));

// we can add new columns on the b-end (e.g. custom header fields), if they are hidden by default, add them to the state
export const reconcileAgGridStateWithColumnDefs = (storedState, columnDefs) => {
  if (!columnDefs || columnDefs.length === 0) {
    return {}; // No column definitions means we can't have a valid state
  }

  if (!storedState) {
    return storedState;
  }

  const currentColIds = columnDefs.map((col) => col.field).filter(Boolean);

  // --- Reconcile Column Order ---
  const storedOrderedColIds = storedState?.columnOrder?.orderedColIds || [];

  // Remove obsolete columns from stored order
  const validOrderedColIds = storedOrderedColIds.filter((colId) => currentColIds.includes(colId));

  // Find new columns not in stored state
  const newColumns = columnDefs.filter((col) => !storedOrderedColIds.includes(col.field));

  // Insert new columns at the end (could be customized to insert in specific positions)
  const reconciledOrderedColIds = [...validOrderedColIds, ...newColumns.map((col) => col.field)];

  // --- Reconcile Column Visibility ---
  const storedHiddenColIds = storedState?.columnVisibility?.hiddenColIds || [];

  // Keep hidden columns that still exist
  const validHiddenColIds = storedHiddenColIds.filter((colId) => currentColIds.includes(colId));

  // Add new columns that should be hidden by default
  const newHiddenColIds = newColumns.filter((col) => col.hide).map((col) => col.colId || col.field);

  const reconciledHiddenColIds = [...validHiddenColIds, ...newHiddenColIds];

  // --- Build Reconciled State ---
  return {
    columnOrder: {
      orderedColIds: reconciledOrderedColIds,
    },
    columnVisibility: {
      hiddenColIds: reconciledHiddenColIds,
    },
  };
};

const formatNumberToFloat = (value) => compose(splitNumberByCommas, convertToFloatNumber)(value);

export const valueFormatterNumber = ({ value }) =>
  typeof value === 'number' ? formatNumberToFloat(value) : SYMBOL_FOR_EMPTY_CELL;

export const valueFormatterAmount =
  (shouldHighlightAmount) =>
  ({ value }) => {
    if (typeof value === 'number') {
      const isHighlightNegative = shouldHighlightAmount && value < 0;
      const formattedString = formatNumberToFloat(isHighlightNegative ? Math.abs(value) : value);

      return isHighlightNegative ? `(${formattedString})` : formattedString;
    }

    // b-end return zero like string '0.0' - handle this case
    if (typeof value === 'string' && value.length > 0) {
      return formatNumberToFloat(Number(value));
    }

    return SYMBOL_FOR_EMPTY_CELL;
  };
